import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { useRef } from 'react';
import { LOGO_URL, HERO_BG_IMAGE } from '../data/business';
import { DOORDASH_URL, UBEREATS_URL } from '../data/menu';

export function HeroSection() {
  const { t, isRTL } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  const scrollToMenu = () => {
    document.getElementById('dishes')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToCatering = () => {
    document.getElementById('catering')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Text animation variants - only for English
  const wordVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  const headline = t('hero.headline');

  return (
    <section 
      ref={ref}
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax Background Image */}
      <motion.div 
        className="absolute inset-0"
        style={{ y, scale }}
      >
        <img
          src={HERO_BG_IMAGE}
          alt="Charcoal Grill"
          className="w-full h-full object-cover"
        />
        {/* Animated Gradient Overlay */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/40 to-white/70"
          animate={{
            background: [
              "linear-gradient(to bottom, rgba(255,255,255,0.6), rgba(255,255,255,0.4), rgba(255,255,255,0.7))",
              "linear-gradient(to bottom, rgba(255,255,255,0.5), rgba(255,255,255,0.3), rgba(255,255,255,0.6))",
              "linear-gradient(to bottom, rgba(255,255,255,0.6), rgba(255,255,255,0.4), rgba(255,255,255,0.7))"
            ]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      {/* Charcoal Grill Smoke — Lightweight */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]" aria-hidden="true">
        {/* Bottom haze */}
        <div className="smoke-haze" />

        {/* 3 optimized smoke clouds */}
        <div
          className="smoke-layer"
          style={{
            left: '10%',
            width: '280px',
            height: '300px',
            background: 'radial-gradient(ellipse, rgba(180,170,155,0.35) 0%, transparent 70%)',
            '--rise-dur': '14s',
            '--sway-dur': '10s',
            '--pulse-dur': '7s',
            '--delay': '0s',
            '--smoke-peak': '0.35',
            '--smoke-mid': '0.18',
            '--blur-min': '25px',
            '--blur-max': '45px',
          }}
        />
        <div
          className="smoke-layer"
          style={{
            left: '45%',
            width: '260px',
            height: '320px',
            background: 'radial-gradient(ellipse, rgba(175,165,150,0.32) 0%, transparent 70%)',
            '--rise-dur': '16s',
            '--sway-dur': '12s',
            '--pulse-dur': '8s',
            '--delay': '2s',
            '--smoke-peak': '0.32',
            '--smoke-mid': '0.16',
            '--blur-min': '22px',
            '--blur-max': '42px',
          }}
        />
        <div
          className="smoke-layer"
          style={{
            left: '75%',
            width: '240px',
            height: '280px',
            background: 'radial-gradient(ellipse, rgba(185,175,160,0.30) 0%, transparent 70%)',
            '--rise-dur': '15s',
            '--sway-dur': '11s',
            '--pulse-dur': '7s',
            '--delay': '4s',
            '--smoke-peak': '0.30',
            '--smoke-mid': '0.15',
            '--blur-min': '20px',
            '--blur-max': '40px',
          }}
        />
      </div>

      {/* Floating Particles — Reduced */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`particle-${i}`}
            className="absolute w-2 h-2 bg-[#6B1C23]/20 rounded-full"
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + (i % 3) * 20}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.6, 0.3],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.3,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      {/* Content */}
      <motion.div 
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        style={{ opacity }}
      >
        {/* Animated Headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-6"
        >
          <h1
            className={`text-4xl sm:text-5xl lg:text-7xl font-semibold tracking-tight ${
              isRTL ? 'font-cairo' : 'font-cormorant'
            }`}
            data-testid="hero-headline"
            style={{
              color: '#6B1C23',
              textShadow: '2px 2px 8px rgba(255,255,255,1), -2px -2px 8px rgba(255,255,255,1), 0 0 30px rgba(255,255,255,0.8)'
            }}
          >
            {headline}
          </h1>
        </motion.div>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className={`text-lg sm:text-xl mb-12 max-w-2xl mx-auto ${
            isRTL ? 'font-cairo' : 'font-manrope'
          }`}
          style={{
            color: '#4A4A4A',
            textShadow: '1px 1px 4px rgba(255,255,255,1), -1px -1px 4px rgba(255,255,255,1)'
          }}
          data-testid="hero-subheadline"
        >
          {t('hero.subheadline')}
        </motion.p>

        {/* Animated Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <motion.div
            whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(107, 28, 35, 0.3)" }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              asChild
              size="lg"
              className="bg-[#6B1C23] text-white hover:bg-[#8B2430] transition-all px-8 py-6 text-lg font-manrope shadow-lg"
              data-testid="hero-order-doordash-btn"
            >
              <a href={DOORDASH_URL} target="_blank" rel="noopener noreferrer">
                {t('hero.orderNow')} — DoorDash
              </a>
            </Button>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(20, 35, 40, 0.3)" }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              asChild
              size="lg"
              className="bg-[#142328] text-white hover:bg-[#1f3640] transition-all px-8 py-6 text-lg font-manrope shadow-lg"
              data-testid="hero-order-ubereats-btn"
            >
              <a href={UBEREATS_URL} target="_blank" rel="noopener noreferrer">
                {t('hero.orderNow')} — Uber Eats
              </a>
            </Button>
          </motion.div>
          
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              size="lg"
              onClick={scrollToMenu}
              className="bg-white/90 text-[#6B1C23] hover:bg-white transition-all px-8 py-6 text-lg font-manrope shadow-lg border border-white/50 backdrop-blur-sm font-semibold"
              data-testid="hero-menu-btn"
            >
              {t('hero.viewMenu')}
            </Button>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(107, 28, 35, 0.3)" }}
            whileTap={{ scale: 0.95 }}
            className="sm:hidden"
          >
            <Button
              size="lg"
              onClick={scrollToCatering}
              className="bg-[#6B1C23] text-white hover:bg-[#8B2430] transition-all px-8 py-6 text-lg font-manrope shadow-lg font-semibold"
              data-testid="hero-catering-btn"
            >
              Catering & Events
            </Button>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Animated Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.button
          animate={{ 
            y: [0, 15, 0],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          onClick={scrollToMenu}
          className="text-[#6B1C23] hover:text-[#8B2430] transition-colors"
          data-testid="scroll-indicator"
        >
          <ChevronDown className="w-10 h-10" />
        </motion.button>
      </motion.div>
    </section>
  );
}
